<template>
  <section :class="$style.page">
    <RouterLink :to="{ name: 'login' }">
      <img
        src="@/assets/images/logo.svg?url"
        :class="[$style.page__logo, $style.logo]"
      />
    </RouterLink>

    <template v-if="!sent">
      <header :class="[$style.page__header, $style.header]">
        <h2 :class="$style.header__title">パスワードをお忘れですか？</h2>
        <p :class="$style.header__body">
          登録されているメールアドレスを入力してください。 <br />
          アドレス宛に新しいパスワードの設定方法をお送りします。
        </p>
      </header>
      <section :class="$style.page__main">
        <RequestForm
          @submit="onSubmit"
          sendButtonText="パスワード再設定メールの送信"
          inputType="email"
          v-model="email"
          inputName="メールアドレス"
          :submitDisabled="isLoading"
        />
      </section>
    </template>

    <template v-else>
      <header :class="[$style.page__header, $style.header]">
        <h2 :class="$style.header__title">メールを送信しました</h2>
        <p :class="$style.header__body">
          パスワード再設定のURLを、入力いただいたメールアドレスに送信しました。<br />
          メールに記載されているURLにアクセスし、パスワードを再設定してください。
        </p>
      </header>
      <section :class="[$style.page__main, $style.confirmation]">
        <h3 :class="$style.confirmation__title">
          ご入力いただいたメールアドレス
        </h3>
        <p :class="$style.confirmation__value">{{ email }}</p>
      </section>

      <section :class="[$style.page__main, $style.main]">
        <h2 :class="$style.main__title">メールが届かない場合</h2>
        <p :class="$style.main__body">
          メールが届かない場合、入力いただいたメールアドレスが登録されていないか、誤ったメールアドレスが設定されている可能性がございます。<br />
          上記に思い当たる方は、御手数ですが<ExternalLink
            :href="SHADOW_CONTACT_URL"
            >お問い合わせ</ExternalLink
          >よりご相談下さい。
        </p>
      </section>
    </template>

    <footer :class="[$style.page__footer, $style.footer]">
      <RouterLink :to="{ name: 'login' }"> ログインページへ戻る </RouterLink>
    </footer>
  </section>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import ExternalLink from '@/components/atoms/ExternalLink.vue'

import RequestForm from './RequestForm.vue'

import { SHADOW_CONTACT_URL } from '@/config'
import { useAlert } from '@/libs/alertPortal'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { captureException } from '@sentry/vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import { useRequestChangePasswordWithoutLoggedInMutation } from './PasswordResetRequestPage.generated'

useHead({
  title: 'パスワード変更申請',
})

const route = useRoute('resetPasswordVerify')

const emailQuery = route.query['email']

const email = ref(typeof emailQuery === 'string' ? emailQuery : '')
const isLoading = ref(false)
const sent = ref(false)

const alert = useAlert()

const requestChangePasswordWithoutLoggedInMutation =
  useRequestChangePasswordWithoutLoggedInMutation()

const onSubmit = async (): Promise<void> => {
  try {
    isLoading.value = true
    const result = await requestChangePasswordWithoutLoggedInMutation.mutate({
      email: email.value,
    })
    if (result?.data == null) {
      throw new Error('requestChangePasswordWithoutLoggedInMutation failed')
    }
    if (result.data.requestChangePasswordWithoutLoggedIn.success) {
      sent.value = true
    } else if (result.data.requestChangePasswordWithoutLoggedIn.message) {
      alert.error(result.data.requestChangePasswordWithoutLoggedIn.message)
    }
  } catch (error) {
    alert.error('申し訳ございませんが、エラーです。')

    if (!isClientError(error)) {
      captureException(error)
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" module>
.page {
  &__header {
    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  &__main {
    width: 480px;
    padding: 40px;
    margin-right: auto;
    margin-left: auto;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-v2-base;

    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  &__footer {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }
}

.logo {
  display: block;
  width: 320px;
  margin-right: auto;
  margin-left: auto;
}

.header {
  text-align: center;

  &__title {
    font-size: 24px;
    font-weight: bold;
  }

  &__body {
    font-size: 14px;
    line-height: 24px;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.confirmation {
  text-align: center;

  &__title {
    color: $color-text-and-object-high-emphasis;
    font-size: 14px;
    font-weight: bold;
  }

  &__value {
    color: $color-text-and-object-accent-primary;
    font-size: 18px;
    font-weight: bold;
  }
}

.main {
  text-align: center;

  &__title {
    font-size: 16px;
    font-weight: bold;
  }

  &__body {
    font-size: 12px;
    line-height: 20px;

    &:nth-child(n + 2) {
      margin-top: 20px;
    }
  }
}

.footer {
  font-size: 14px;
  text-align: center;
}
</style>
